import { memo, useMemo } from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { useRolesForContext } from "@vega-api/extended/auth/roles/useRolesForContext";
import { type RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { type SubjectType } from "@vega-api/generated/types/subjectType";
import { type UserPermissionEntry } from "@vega-api/generated/types/userPermissionEntry";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { EmptyFundsList } from "../../../components/EmptyFundsList";
import { FundCell } from "../../../components/FundCell";
import { RoleSelector } from "../../../components/RoleSelector";
import { groupPermissionsBySubject } from "../../../helpers/groupPermissions";

import { EditRole } from "./EditRole";

type Props = {
  permissions: UserPermissionEntry[];
  subjectType: SubjectType;
  isLoading?: boolean;
  pendingSubjectId?: string | null;
  onRoleChange?: (selectedRole: RoleNew, subjectId: string) => void;
  onRemoveAccess?: (subjectId: string) => void;
  roleContext: RoleContext;
};

export const FundsList = memo(
  ({
    permissions,
    subjectType,
    isLoading,
    pendingSubjectId,
    onRoleChange,
    onRemoveAccess,
    roleContext,
  }: Props) => {
    const { getRoleOptionsAsRoles } = useRolesForContext(roleContext);
    const roleOptions = getRoleOptionsAsRoles();

    const { manageablePermissions, readOnlyPermissions } = useMemo(() => {
      const filtered = permissions.filter(
        (item) => item.subjectType === subjectType,
      );

      const { manageable, readOnly } = filtered.reduce<{
        manageable: UserPermissionEntry[];
        readOnly: UserPermissionEntry[];
      }>(
        (acc, item) => {
          if (item.manageable) {
            acc.manageable.push(item);
          } else {
            acc.readOnly.push(item);
          }

          return acc;
        },
        { manageable: [], readOnly: [] },
      );

      return {
        manageablePermissions: groupPermissionsBySubject(manageable),
        readOnlyPermissions: groupPermissionsBySubject(readOnly),
      };
    }, [permissions, subjectType]);

    const fundColumn = (
      headerText: string,
    ): ColumnDef<ReturnType<typeof groupPermissionsBySubject>[0]> => ({
      id: "fund",
      accessorKey: "name",
      cell: ({ row }) => (
        <FundCell
          assetClass={row.original.assetClass}
          subjectName={row.original.subjectName}
        />
      ),
      header: headerText,
    });

    const roleColumn: ColumnDef<
      ReturnType<typeof groupPermissionsBySubject>[0]
    > = {
      id: "role",
      accessorKey: "selectedRole",
      cell: ({ row }) => {
        const { selectedRole, subjectId, subjectName, manageable } =
          row.original;
        const roleOption = roleOptions.find(
          (option) => manageable && option.id === selectedRole,
        );
        const isEditable = roleOption?.customizable;
        const isPending = pendingSubjectId === subjectId;

        return (
          <div className="flex w-full text-right items-center justify-end">
            {isEditable && isTruthy(subjectId) && isTruthy(subjectName) ? (
              <EditRole
                disabled={isPending}
                isLP
                roleId={selectedRole || ""}
                subjectId={subjectId}
                subjectName={subjectName}
              />
            ) : null}
            <RoleSelector
              disabled={!manageable || isPending}
              isLoading={isPending}
              onChange={(option) => {
                if (isTruthy(onRoleChange) && isTruthy(subjectId)) {
                  onRoleChange(option, subjectId);
                }
              }}
              onRemoveAccess={() => {
                if (isTruthy(onRemoveAccess) && isTruthy(subjectId)) {
                  onRemoveAccess(subjectId);
                }
              }}
              options={roleOptions}
              value={selectedRole}
            />
          </div>
        );
      },
      header: m.userManagement_permissions_role(),
      align: "right",
    };

    const columns = [fundColumn(m.common_fund()), roleColumn];
    const readOnlyColumns = [
      fundColumn(m.userManagement_permissions_unauthorizedFunds()),
      roleColumn,
    ];

    if (
      manageablePermissions.length === 0 &&
      readOnlyPermissions.length === 0 &&
      !isLoading
    ) {
      return (
        <EmptyFundsList
          description={m.userManagement_permissions_noInvestments()}
        />
      );
    }

    return (
      <div className="min-h-80 flex flex-col gap-4">
        <CoreTable
          columns={columns}
          data={manageablePermissions}
          dataTestId="user-management-funds-list-table"
          isLoading={isLoading}
          rowClassName="border-none bg-background/primary"
        />
        {readOnlyPermissions.length > 0 && (
          <CoreTable
            columns={readOnlyColumns}
            data={readOnlyPermissions}
            dataTestId="user-management-funds-readonly-table"
            headerClassName="bg-background/tertiary"
            rowClassName="border-none"
            tableClassName="bg-background/tertiary/[.4]"
          />
        )}
      </div>
    );
  },
);
